import React, { useState, useMemo, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Input,
    Row,
} from "reactstrap";
import EcommerceOrdersModal from "components/Common/EcommerceOrdersModal";
import TableContainer from "components/Common/TableContainer";
import TeamAddEditModal from "pages/AllCommonModal/TeamAddEditModal/team-add-edit-modal";
import Spinners from "components/Common/Spinner";
import {
    submitUserTeamFilterData,
    addUserTeam,
    updateUserTeam,
    fetchUserTeamTableAndFilterSchema,
} from "model/UserTeamModel";
import { toast } from "react-toastify";
import Loaders from "components/Common/Loaders";
import { usePermissionContext } from "libs/context/PermissionContext";
import { useCommonFilterOptions } from "libs/context/CommonFilterOptionsContext";
import { DEFAULT_PAGE_SIZE } from "helpers/GeneralHelper";
import { useCommonTableSettingsPageSize } from "hooks/useSettingsReadyTablePageSize";


const Team = () => {
    const { permissionData } = usePermissionContext() || {};
    const { fetchMetadataSchema } = useCommonFilterOptions();
    const { pageSize: settingsPageSize, settingsReady } = useCommonTableSettingsPageSize("Team");
    const initialFilterFetchedRef = useRef(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [teamAddEditModalOpen, setTeamAddEditModalOpen] = useState(false);
    const [modal1, setModal1] = useState(false);
    const [teamData, setTeamData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editableData, setEditableData] = useState(null);
    const [tableMetaData, setTableMetaData] = useState([]);
    const [requestPayload, setRequestPayload] = useState({ pageNumber: 1, pageSize: DEFAULT_PAGE_SIZE });
    const [paginationMeta, setPaginationMeta] = useState({
        pageNumber: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        totalPages: 0,
        totalRecords: 0,
    });
    const toggleViewModal = () => setModal1(!modal1);

    const defaultTableMetaData = [
        { Header: "ID", accessor: "id" },
        { Header: "Name", accessor: "name" },
        { Header: "Is Backend", accessor: "isBackend" },
        { Header: "Status", accessor: "status" },
    ];
    const getTeamTableSchema = async () => {
        const response = await fetchMetadataSchema("metadata:userTeam", () =>
            fetchUserTeamTableAndFilterSchema({})
        );
        const columns = response?.data?.table?.columns || [];
        if (Array.isArray(columns) && columns.length) {
            setTableMetaData(
                columns.map((col) => ({
                    Header: col?.title,
                    accessor: col?.field,
                    type: col?.type,
                }))
            );
            return;
        }
        setTableMetaData(defaultTableMetaData);
    };

    const filteredData = useMemo(() => teamData, [teamData]);

    const teamActionPermissions = useMemo(
        () => ({
            canCreate: Boolean(permissionData?.TeamAddAccess),
            canEdit: Boolean(permissionData?.TeamUpdateAccess),
        }),
        [permissionData]
    );

    const hasAnyTeamRowAction = useMemo(
        () => teamActionPermissions.canEdit,
        [teamActionPermissions]
    );

    const getTeamList = async (payloadOverrides = {}) => {
        setLoading(true);
        const payload = {
            ...requestPayload,
            ...payloadOverrides,
        };
        const response = await submitUserTeamFilterData(payload);
        const responseData = response?.data || {};
        const listData = responseData?.records || responseData || [];
        const mappedData = (Array.isArray(listData) ? listData : []).map((item) => ({
            ...item,
            id: item?.id || item?._id || "",
            name: item?.name || "",
            isBackend: Boolean(item?.isBackend ?? item?.isbackend ?? false),
            status: String(item?.status || "").toLowerCase() === "active" ? "active" : "deactive",
        }));

        if (response) {
            setTeamData(mappedData);
            setPaginationMeta({
                pageNumber: Number(responseData?.pageNumber) || Number(payload?.pageNumber) || 1,
                pageSize: Number(responseData?.pageSize) || Number(payload?.pageSize) || DEFAULT_PAGE_SIZE,
                totalPages: Number(responseData?.totalPages) || 0,
                totalRecords: Number(responseData?.totalRecords) || 0,
            });
            setRequestPayload(payload);
        } else {
            setTeamData([]);
        }
        setLoading(false);
    };

    useEffect(() => {
        getTeamTableSchema();
    }, []);

    useEffect(() => {
        if (!settingsReady || initialFilterFetchedRef.current) return;
        initialFilterFetchedRef.current = true;
        const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;
        setRequestPayload((previous) => ({ ...previous, pageSize }));
        setPaginationMeta((previous) => ({ ...previous, pageSize }));
        getTeamList({ pageNumber: 1, pageSize });
    }, [settingsReady, settingsPageSize]);

    const handleSearchSubmit = async (value) => {
        const trimmedValue = value?.trim() || "";
        await getTeamList({
            ...requestPayload,
            pageNumber: 1,
            nameContains: trimmedValue,
        });
    };

    const handleEdit = (row) => {
        setEditableData(row || null);
        setTeamAddEditModalOpen(true);
    };

    const handleAddEditSubmit = async (teamFormData = {}) => {
        const payload = {
            id: teamFormData?.id || "",
            name: String(teamFormData?.name || "").trim(),
            status: teamFormData?.status || "active",
            isBackend: !!teamFormData?.isBackend,
        };
        const response = payload?.id
            ? await updateUserTeam(payload?.id, payload)
            : await addUserTeam(payload);

        if (response?.success || response?.isSuccess) {
            toast.success(response?.message || "Team saved successfully");
            setTeamAddEditModalOpen(false);
            setEditableData(null);
            await getTeamList({ pageNumber: 1, pageSize: paginationMeta?.pageSize || DEFAULT_PAGE_SIZE });
            return;
        }
        toast.error(response?.message || "Unable to save team");
    };

    const handlePageChange = async (page) => {
        await getTeamList({ pageNumber: page });
    };

    const handleRowsPerPageChange = async (rows) => {
        await getTeamList({ pageNumber: 1, pageSize: rows });
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <EcommerceOrdersModal isOpen={modal1} toggle={toggleViewModal} />

                    <Card>
                        <CardBody>
                            <div className="d-flex justify-content-between align-items-center">
                                <h5 className="fw-bold mb-3">Manage Team</h5>
                                {teamActionPermissions.canCreate ? (
                                    <Col xs="12" md="4" className="text-md-end mt-2 mt-md-0">
                                        <Button
                                            color="primary"
                                            onClick={() => {
                                                setEditableData(null);
                                                setTeamAddEditModalOpen(true);
                                            }}
                                        >
                                            + Create
                                        </Button>
                                    </Col>
                                ) : null}
                            </div>
                            <hr />

                            <Row className="align-items-center g-2 mb-4">
                                <Col xs="12" md="8">
                                    <div className="d-flex border rounded w-100 category-search-input">
                                        <Input
                                            type="text"
                                            placeholder="Search by name"
                                            className="border-0"
                                            value={searchQuery}
                                            onChange={(e) => {
                                                const nextValue = e?.target?.value || "";
                                                setSearchQuery(nextValue);
                                                if (!nextValue.trim()) {
                                                    handleSearchSubmit("");
                                                }
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    handleSearchSubmit(searchQuery?.trim());
                                                }
                                            }}
                                        />
                                        <Button
                                            className="search-icon-button"
                                            onClick={() => handleSearchSubmit(searchQuery?.trim())}
                                        >
                                            <i className="fa fa-search"></i>
                                        </Button>
                                    </div>
                                </Col>
                            </Row>

                            {!loading && tableMetaData?.length ? (
                                <TableContainer
                                    columns={tableMetaData}
                                    data={filteredData}
                                    isActionRequired={hasAnyTeamRowAction}
                                    onEdit={
                                        teamActionPermissions.canEdit ? handleEdit : undefined
                                    }
                                    isEditAccess={teamActionPermissions.canEdit}
                                    isCheckboxLeftSide={false}
                                    isPagination={true}
                                    currentPage={paginationMeta?.pageNumber}
                                    rowsPerPage={paginationMeta?.pageSize}
                                    totalPages={paginationMeta?.totalPages}
                                    totalRecords={paginationMeta?.totalRecords}
                                    onPageChange={handlePageChange}
                                    onPageSizeChange={handleRowsPerPageChange}
                                    pageName={"Team"}
                                />
                            ) : (
                                // <Card className="border mb-3">
                                //     <CardBody>
                                //         <Spinners setLoading={setLoading} />
                                //     </CardBody>
                                // </Card>
                                <Loaders />
                            )}
                        </CardBody>
                    </Card>

                    <TeamAddEditModal
                        teamAddEditModalOpen={teamAddEditModalOpen}
                        setTeamAddEditModalOpen={setTeamAddEditModalOpen}
                        editableData={editableData}
                        onSubmit={handleAddEditSubmit}
                    />
                </Container>
            </div>
        </React.Fragment>
    );
};

Team.propTypes = {
    orders: PropTypes.array,
    onGetOrders: PropTypes.func,
};

export default withRouter(Team);
